import React, { useState, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import {
  queryInvoicesForResellers,
  buildTrackerPK,
  InvoiceRecord,
} from "src/config/invoice-tracker-client";
import { reconcileRow } from "./reconcileInvoices";
import {
  Alert,
  Box,
  Button,
  DatePicker,
  FormField,
  Grid,
  Header,
  Multiselect,
  MultiselectProps,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  Tabs,
} from "@amzn/awsui-components-console";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  FixedFeeConfig,
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
  GetPlanCommand,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import { AuthService } from "src/auth/auth-service";
import { FullPageTitle } from "src/components/header/title";
import {
  createAllRegionClients,
  listAllRegionConfigs,
  RegionConfig,
} from "src/config/facd-all-regions";
import { isProd } from "src/util/is-prod";
import "src/index.scss";

import { ReportRow, CcTotalRow } from "./types";
import {
  addMonths,
  getFirstOfMonth,
  getDefaultReportDate,
  mapWithConcurrency,
  populateFunctionalFields,
  computeEndingBalances,
} from "./utils";
import { computeReportRow } from "./computeReportRow";
import { combineRowsByTrackerPK } from "./reportOverrides";
import {
  ST_COLUMNS,
  LT_COLUMNS,
  CC_SUBTOTAL_COLUMNS,
  STATUS_COLORS,
} from "./columnDefinitions";
import { exportStCsv, exportLtCsv } from "./csvExport";

const legendLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#555",
};
const legendBadgeStyle: React.CSSProperties = {
  padding: "2px 8px",
  borderRadius: "3px",
  fontSize: "12px",
};
const STATUS_SORT_ORDER: Record<string, number> = {
  confirmed: 0,
  "tracker-sourced": 1,
  "amount-mismatch": 2,
  unreconciled: 3,
};

export const AppUnbilledArAging: React.FC = () => {
  const [authenticating, setAuthenticating] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [ldap, setLdap] = useState("");
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  // True only when a silent run actually refetches (Refresh); a date-change recompute reads from cache and shows a plain label with no count.
  const [recomputeShowCount, setRecomputeShowCount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [progress, setProgress] = useState({ fetched: 0, total: 0 });
  // Default to last day of previous month (month-end close); defaulting to today overstates Invoices Issued.
  // UTC intentional: F3S adjustments fire at UTC midnight on the 1st of each month.
  const [reportDate, setReportDate] = useState(getDefaultReportDate);
  const [selectedResellers, setSelectedResellers] = useState<
    MultiselectProps.Option[]
  >([]);
  const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<
    MultiselectProps.Option[]
  >([]);
  const [expandedItems, setExpandedItems] = useState<ReportRow[]>([]);

  const authObj = useRef(new AuthService());
  // Cache of date-independent FACD data (full config + ARD enrichment) keyed by fixedFeeId so date-only re-runs skip GetFixedFeeConfig/GetPlan calls.
  const configCacheRef = useRef(
    new Map<
      string,
      {
        fullConfig: FixedFeeConfig;
        ardInfo: {
          companyCode: string;
          funcCurrency: string;
          channelCode: string;
        };
      }
    >(),
  );
  // Cache of InvoiceTracker records keyed by tracker PK, fetched over [boundary, now]; date changes filter this client-side so no new DDB query is sent per date.
  const trackerCacheRef = useRef(new Map<string, InvoiceRecord[]>());
  // Monotonic run token; a run whose id is superseded by a newer run discards its results so overlapping runs can't write stale rows.
  const runIdRef = useRef(0);

  useEffect(() => {
    const init = async () => {
      await authObj.current.authenticate();
      setAuthenticating(false);
      setIsLoggedIn(authObj.current.getIsLoggedIn());
      setLdap(authObj.current.ldap);
      const userLdap = authObj.current.ldap;
      if (
        authObj.current.getIsLoggedIn() &&
        (userLdap.includes("bart-read-only") || userLdap.includes("bart-admin"))
      ) {
        await generateReport(reportDate);
      }
    };
    init();
  }, []);

  // Recompute in place from the cache when the report date changes — no FACD refetch, table stays visible. Debounced and guarded against partial dates typed into the picker.
  useEffect(() => {
    if (configCacheRef.current.size === 0) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDate)) return;
    const timer = setTimeout(() => {
      generateReport(reportDate, { silent: true });
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDate]);

  const generateReport = async (
    date: string,
    opts: { refresh?: boolean; silent?: boolean } = {},
  ) => {
    const { refresh = false, silent = false } = opts;
    // Claim this run's token; progress callbacks and the final setRows are gated on it so a superseded run is discarded.
    const runId = ++runIdRef.current;
    // refresh clears both caches to force a full refetch; silent keeps the current table visible while recomputing from cache.
    if (refresh) {
      configCacheRef.current.clear();
      trackerCacheRef.current.clear();
    }
    if (silent) {
      // Only a cache-clearing Refresh refetches, so only it shows the x/total count; a date-change recompute reads from cache and shows a plain label.
      setRecomputeShowCount(refresh);
      setRecomputing(true);
    } else setLoading(true);
    setError(null);
    if (!silent) setRows([]);
    try {
      const clients = await createAllRegionClients();

      const reclassCutoff = addMonths(getFirstOfMonth(date), 1);

      const regionConfigs = await listAllRegionConfigs(clients);
      // A superseded run must not reset the progress counter a newer run is displaying.
      if (runId !== runIdRef.current) return;
      const activeConfigs = regionConfigs.filter(
        (rc) =>
          rc.config.dealStatus === "ENABLED" &&
          rc.config.environment === "PRODUCTION",
      );
      setProgress({ fetched: 0, total: activeConfigs.length });

      const lookupArd = async (
        planIds: string[],
        client: FuseAccountingCentralDatastore,
      ): Promise<{
        companyCode: string;
        funcCurrency: string;
        channelCode: string;
        // False only when a GetPlan/ARD call threw; a clean "no ARD found" fall-through is still true (and cacheable).
        enrichmentSucceeded: boolean;
      }> => {
        let hadError = false;
        for (const planId of planIds) {
          try {
            const planResp = await client.send(new GetPlanCommand({ planId }));
            const plan = (planResp as any).plan ?? planResp;
            const ardId = plan?.ardId;
            if (!ardId) continue;
            // ARD is embedded in the plan response — no separate GetArd call needed
            const ard = plan?.ard;
            if (!ard) continue;
            // Extract company code from FIXED_FEE_RECLASS_UNBILLED_AR use case (GL 13003/18203)
            let companyCode = "";
            let channelCode = "";
            for (const uc of ard?.usecases ?? []) {
              if (uc?.name === UseCaseName.FIXED_FEE_RECLASS_UNBILLED_AR) {
                for (const line of uc?.uslLines ?? []) {
                  if (line?.companyCode) {
                    companyCode = line.companyCode;
                    channelCode = line.channel ?? "";
                    break;
                  }
                }
                break;
              }
            }
            // Fallback: first company code from any use case
            if (!companyCode) {
              for (const uc of ard?.usecases ?? []) {
                for (const line of uc?.uslLines ?? []) {
                  if (line?.companyCode) {
                    companyCode = line.companyCode;
                    break;
                  }
                }
                if (companyCode) break;
              }
            }
            // Fallback: first channel code from any use case (ARDs without FIXED_FEE_RECLASS_UNBILLED_AR)
            if (!channelCode) {
              for (const uc of ard?.usecases ?? []) {
                for (const line of uc?.uslLines ?? []) {
                  if (line?.channel) {
                    channelCode = line.channel;
                    break;
                  }
                }
                if (channelCode) break;
              }
            }
            const result = {
              companyCode,
              funcCurrency: ard?.functionalCurrency ?? "USD",
              channelCode,
              enrichmentSucceeded: true,
            };
            return result;
          } catch (err) {
            hadError = true;
            console.error(`ARD lookup failed for plan ${planId}:`, err);
          }
        }
        // Clean fall-through (no plan had an ARD) is cacheable; a thrown GetPlan error is not, so it retries next run.
        return {
          companyCode: "",
          funcCurrency: "USD",
          channelCode: "",
          enrichmentSucceeded: !hadError,
        };
      };

      // Raised from 5 and switched from fixed Promise.all waves to a continuous pool that no longer stalls each wave on its slowest member.
      const CONCURRENCY = 15;

      // Builds one row, reusing cached date-independent FACD data (full config + ARD) so date-only re-runs skip all GetFixedFeeConfig/GetPlan calls.
      const buildRow = async ({
        config,
        region,
      }: RegionConfig): Promise<ReportRow | null> => {
        const fixedFeeId = config.fixedFeeId;
        if (!fixedFeeId) return null;
        try {
          let cached = configCacheRef.current.get(fixedFeeId);
          if (!cached) {
            const client = clients[region];
            const resp = await client.send(
              new GetFixedFeeConfigCommand({ fixedFeeId }),
            );
            if (!resp.fixedFeeConfig) return null;
            const planIds = resp.fixedFeeConfig.dealConfig?.fusePlanIds ?? [];
            const { enrichmentSucceeded, ...ardInfo } = await lookupArd(
              planIds,
              client,
            );
            cached = { fullConfig: resp.fixedFeeConfig, ardInfo };
            // Only cache a config whose ARD enrichment completed cleanly, so a transient failure retries next run instead of poisoning the cache with empty company/channel codes.
            if (enrichmentSucceeded)
              configCacheRef.current.set(fixedFeeId, cached);
          }
          const row = computeReportRow(cached.fullConfig, date, reclassCutoff);
          if (!row) return null;
          row.companyCode = cached.ardInfo.companyCode;
          row.funcCurrency = cached.ardInfo.funcCurrency;
          row.channelCode = cached.ardInfo.channelCode;
          row.fxRate =
            row.currencyCode !== cached.ardInfo.funcCurrency ? row.fxRate : 1.0;
          row.region = region;
          return row;
        } catch (err) {
          console.error(`Failed to fetch config ${fixedFeeId}:`, err);
          return null;
        }
      };

      const builtRows = await mapWithConcurrency(
        activeConfigs,
        CONCURRENCY,
        (rc) => buildRow(rc),
        (completed) => {
          if (runId === runIdRef.current)
            setProgress({ fetched: completed, total: activeConfigs.length });
        },
      );
      const reportRows: ReportRow[] = builtRows.filter(
        (r): r is ReportRow => r !== null,
      );

      reportRows.sort(
        (a, b) =>
          a.companyCode.localeCompare(b.companyCode) ||
          a.resellerId.localeCompare(b.resellerId),
      );

      // Collapse configs sharing one InvoiceTracker PK into a combined parent row
      // (after channelCode enrichment, before reconcile) so a shared invoice is
      // deducted once. Per-config detail is retained in each parent's subRows.
      const combinedRows = combineRowsByTrackerPK(reportRows, (r) =>
        buildTrackerPK(r.resellerId, r.serviceProvider, r.channelCode),
      );

      // Reconcile against InvoiceTracker DDB
      let reconciledRows = combinedRows;
      try {
        const trackerKeys = Array.from(
          new Set(
            combinedRows
              .map((r) =>
                buildTrackerPK(r.resellerId, r.serviceProvider, r.channelCode),
              )
              .filter(Boolean) as string[],
          ),
        );
        // Build boundary map: per reseller, use the earliest firstInvoiceBreakdownPeriod
        const boundaries: Record<string, string> = {};
        for (const row of combinedRows) {
          if (!row.resellerId || !row.firstInvoiceBreakdownPeriod) continue;
          const pk = buildTrackerPK(
            row.resellerId,
            row.serviceProvider,
            row.channelCode,
          );
          if (!pk) continue;
          const existing = boundaries[pk];
          if (!existing || row.firstInvoiceBreakdownPeriod < existing) {
            boundaries[pk] = row.firstInvoiceBreakdownPeriod;
          }
        }
        // Fetch any uncached PKs up to the current month; the cache spans [boundary, now] so date changes filter client-side with no new DDB query.
        const nowMonth = new Date().toISOString().slice(0, 7);
        const missingKeys = trackerKeys.filter(
          (pk) => !trackerCacheRef.current.has(pk),
        );
        if (missingKeys.length > 0) {
          const fetchedData = await queryInvoicesForResellers(
            missingKeys,
            boundaries,
            nowMonth,
          );
          for (const [pk, records] of Object.entries(fetchedData)) {
            if (records != null) trackerCacheRef.current.set(pk, records);
          }
        }
        const reportMonth = date.substring(0, 7);
        reconciledRows = combinedRows.map((row) => {
          const pk = buildTrackerPK(
            row.resellerId,
            row.serviceProvider,
            row.channelCode,
          );
          if (!pk) return row;
          const cachedRecords = trackerCacheRef.current.get(pk);
          if (cachedRecords == null) return row;
          // Client-side upper bound to the report month yields the identical set the per-date query returned.
          const records = cachedRecords.filter(
            (r) => r.billingPeriod <= reportMonth,
          );
          const tacPeriods = row.tacActiveBillingPeriods
            ? new Set(row.tacActiveBillingPeriods)
            : undefined;
          return reconcileRow(row, records, tacPeriods);
        });
      } catch (err) {
        console.error(
          "InvoiceTracker reconciliation failed, showing report without status:",
          err,
        );
      }

      // Populate functional currency fields for all rows (reconciled or not).
      // Children are per-config detail and are never reconciled individually: the
      // shared invoice is reconciled ONCE on the parent (r). So for each child we
      //   - blank Invoices Issued (isSubRow -> rendered "n/a"; underlying set to 0),
      //   - show ST ending GROSS = ST issuance + reclass (no invoice deduction),
      //   - take FX rate and functional currency from the parent's reconciled,
      //     invoice-derived values so functional columns share one basis.
      // CONSEQUENCE (by design): the children's ST endings do NOT sum to the
      // parent's ST ending -- the difference is exactly the single shared invoice,
      // which the parent totals the children and then subtracts once. LT ending has
      // no invoice term, so it stays real per-config and does sum to the parent.
      // With no per-child invoice, each child's FX revaluation is 0.
      reconciledRows.forEach((r) => {
        populateFunctionalFields(r);
        r.subRows?.forEach((child) => {
          child.isSubRow = true;
          child.funcCurrency = r.funcCurrency;
          child.fxRate = r.fxRate;
          child.invoicesIssued = 0;
          child.functionalInvoicesIssued = 0;
          Object.assign(child, computeEndingBalances(child));
          populateFunctionalFields(child);
          child.invoiceStatus = r.invoiceStatus;
        });
      });

      // Sort by status: confirmed first, then yellow, orange, red
      reconciledRows.sort(
        (a, b) =>
          (STATUS_SORT_ORDER[a.invoiceStatus] ?? 9) -
            (STATUS_SORT_ORDER[b.invoiceStatus] ?? 9) ||
          a.companyCode.localeCompare(b.companyCode) ||
          a.resellerId.localeCompare(b.resellerId),
      );

      if (runId === runIdRef.current) setRows(reconciledRows);
    } catch (err: any) {
      if (runId === runIdRef.current) setError(err.message ?? "Unknown error");
    } finally {
      // Only the latest run owns the loading flags; a superseded run must not clear a newer run's spinner.
      if (runId === runIdRef.current) {
        if (silent) setRecomputing(false);
        else setLoading(false);
      }
    }
  };

  // Reseller filter
  const resellerOptions: MultiselectProps.Option[] = Array.from(
    new Set(rows.map((r) => r.resellerId).filter(Boolean)),
  )
    .sort()
    .map((id) => ({ label: id, value: id }));

  // Company code filter
  const companyCodeOptions: MultiselectProps.Option[] = Array.from(
    new Set(rows.map((r) => r.companyCode).filter(Boolean)),
  )
    .sort()
    .map((cc) => ({ label: cc, value: cc }));

  const filteredRows = rows.filter((r) => {
    if (
      selectedResellers.length > 0 &&
      !selectedResellers.some((s) => s.value === r.resellerId)
    )
      return false;
    if (
      selectedCompanyCodes.length > 0 &&
      !selectedCompanyCodes.some((s) => s.value === r.companyCode)
    )
      return false;
    return true;
  });

  // Stable, unique row identity for Table trackBy and expand state. fixedFeeId
  // is display text and not guaranteed unique (combined parents share a label
  // pattern; blank ids collide), so key combined parents by their tracker PK.
  const rowKey = (r: ReportRow): string =>
    r.subRows?.length
      ? `pk:${r.resellerId}#${r.serviceProvider}#${r.channelCode}`
      : `ff:${r.fixedFeeId}`;

  // True config count: a combined parent represents subRows.length configs.
  const configCount = filteredRows.reduce(
    (sum, r) => sum + (r.subRows?.length ?? 1),
    0,
  );

  // Company code totals - one row per company code, sums in functional currency
  const ccTotals: CcTotalRow[] = (() => {
    const grouped: Record<string, CcTotalRow> = {};
    for (const r of filteredRows) {
      const key = `${r.companyCode || "(none)"}|${r.funcCurrency}`;
      if (!grouped[key]) {
        grouped[key] = {
          companyCode: r.companyCode || "(none)",
          configs: 0,
          fcCurrency: r.funcCurrency,
          fcStEnding: 0,
          fcLtEnding: 0,
        };
      }
      grouped[key].configs += r.subRows?.length ?? 1;
      grouped[key].fcStEnding += r.stEnding * r.fxRate;
      grouped[key].fcLtEnding += r.ltEnding * r.fxRate;
    }
    return Object.values(grouped).sort((a, b) =>
      a.companyCode.localeCompare(b.companyCode),
    );
  })();

  const stCollection = useCollection(filteredRows, {
    pagination: { pageSize: 50 },
    sorting: {},
  });
  const ltCollection = useCollection(filteredRows, {
    pagination: { pageSize: 50 },
    sorting: {},
  });

  if (authenticating) return <Spinner size="large" />;

  return (
    <div>
      <FullPageTitle
        title={`Unbilled AR Report${!isProd() ? " (Beta)" : ""}`}
        description="Short-term (GL 13003) and Long-term (GL 18203) unbilled accounts receivable for fixed fee contracts."
      />
      <Box margin={{ top: "l" }} />
      {!(ldap.includes("bart-read-only") || ldap.includes("bart-admin")) ? (
        <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
          <Alert
            header="Only authorized users can access this page!"
            type="error"
          />
        </Grid>
      ) : (
        <Box padding={{ left: "l", right: "l" }}>
          <SpaceBetween direction="vertical" size="l">
            {/* Controls */}
            <SpaceBetween direction="horizontal" size="m" alignItems="end">
              <FormField label="Report Date (end of month)">
                <DatePicker
                  value={reportDate}
                  onChange={({ detail }) => setReportDate(detail.value)}
                  placeholder="YYYY-MM-DD"
                  disabled={loading || recomputing}
                />
              </FormField>
              <FormField label="Reseller ID Filter">
                <Multiselect
                  selectedOptions={selectedResellers}
                  onChange={({ detail }) =>
                    setSelectedResellers([...detail.selectedOptions])
                  }
                  options={resellerOptions}
                  placeholder="All resellers"
                  filteringType="auto"
                  tokenLimit={3}
                />
              </FormField>
              <FormField label="Company Code Filter">
                <Multiselect
                  selectedOptions={selectedCompanyCodes}
                  onChange={({ detail }) =>
                    setSelectedCompanyCodes([...detail.selectedOptions])
                  }
                  options={companyCodeOptions}
                  placeholder="All company codes"
                  filteringType="auto"
                  tokenLimit={3}
                />
              </FormField>
              <Button
                variant="primary"
                onClick={() =>
                  generateReport(reportDate, { refresh: true, silent: true })
                }
                loading={loading || recomputing}
              >
                Refresh
              </Button>
            </SpaceBetween>

            {loading && (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Spinner size="large" />
                <Box>
                  Loading configs... {progress.fetched}/{progress.total}
                </Box>
              </SpaceBetween>
            )}

            {recomputing && (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Spinner />
                <Box>
                  {recomputeShowCount
                    ? `Refreshing... ${progress.fetched}/${progress.total}`
                    : "Recalculating..."}
                </Box>
              </SpaceBetween>
            )}

            {error && (
              <Alert header="Error" type="error">
                {error}
              </Alert>
            )}

            {!loading && rows.length > 0 && (
              <>
                <Box margin={{ bottom: "s" }}>
                  <SpaceBetween direction="horizontal" size="m">
                    <span style={legendLabelStyle}>
                      <strong>Contract ID Legend:</strong>
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS.confirmed,
                      }}
                    >
                      🟢 Confirmed
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS["tracker-sourced"],
                      }}
                    >
                      🟡 Invoice from Tracker (verify GL posting)
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS["amount-mismatch"],
                      }}
                    >
                      🟠 Amount mismatch (investigate)
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        border: "1px solid #ccc",
                      }}
                    >
                      ⚪ Unreconciled
                    </span>
                  </SpaceBetween>
                </Box>
                <Tabs
                  tabs={[
                    {
                      id: "st",
                      label: `GL 13003 - Short Term (${configCount} configs)`,
                      content: (
                        <Table
                          {...stCollection.collectionProps}
                          variant="container"
                          stickyColumns={{ first: 3, last: 0 }}
                          trackBy={rowKey}
                          expandableRows={{
                            getItemChildren: (item) => item.subRows ?? [],
                            isItemExpandable: (item) => !!item.subRows?.length,
                            expandedItems,
                            onExpandableItemToggle: ({ detail }) =>
                              setExpandedItems((prev) =>
                                detail.expanded
                                  ? [...prev, detail.item]
                                  : prev.filter(
                                      (i) => rowKey(i) !== rowKey(detail.item),
                                    ),
                              ),
                          }}
                          header={
                            <Header
                              counter={`(${configCount})`}
                              actions={
                                <Button
                                  onClick={() =>
                                    exportStCsv(filteredRows, reportDate)
                                  }
                                  iconName="download"
                                  // Block export while a run is in flight so the CSV can't capture rows from a superseded report date.
                                  disabled={loading || recomputing}
                                >
                                  Export CSV
                                </Button>
                              }
                            >
                              GL 13003 - Short Term Unbilled AR
                            </Header>
                          }
                          columnDefinitions={ST_COLUMNS}
                          items={stCollection.items}
                          pagination={
                            <Pagination {...stCollection.paginationProps} />
                          }
                        />
                      ),
                    },
                    {
                      id: "lt",
                      label: `GL 18203 - Long Term (${configCount} configs)`,
                      content: (
                        <Table
                          {...ltCollection.collectionProps}
                          variant="container"
                          stickyColumns={{ first: 3, last: 0 }}
                          trackBy={rowKey}
                          expandableRows={{
                            getItemChildren: (item) => item.subRows ?? [],
                            isItemExpandable: (item) => !!item.subRows?.length,
                            expandedItems,
                            onExpandableItemToggle: ({ detail }) =>
                              setExpandedItems((prev) =>
                                detail.expanded
                                  ? [...prev, detail.item]
                                  : prev.filter(
                                      (i) => rowKey(i) !== rowKey(detail.item),
                                    ),
                              ),
                          }}
                          header={
                            <Header
                              counter={`(${configCount})`}
                              actions={
                                <Button
                                  onClick={() =>
                                    exportLtCsv(filteredRows, reportDate)
                                  }
                                  iconName="download"
                                  // Block export while a run is in flight so the CSV can't capture rows from a superseded report date.
                                  disabled={loading || recomputing}
                                >
                                  Export CSV
                                </Button>
                              }
                            >
                              GL 18203 - Long Term Unbilled AR
                            </Header>
                          }
                          columnDefinitions={LT_COLUMNS}
                          items={ltCollection.items}
                          pagination={
                            <Pagination {...ltCollection.paginationProps} />
                          }
                        />
                      ),
                    },
                  ]}
                />

                {/* Company Code Subtotals */}
                <Box>
                  <Header variant="h3">Totals by Company Code</Header>
                  <Table
                    variant="container"
                    columnDefinitions={CC_SUBTOTAL_COLUMNS}
                    items={ccTotals}
                  />
                </Box>
              </>
            )}

            {!loading && rows.length === 0 && !error && (
              <Alert header="No data" type="info">
                No active fixed fee configs found for report date {reportDate}.
              </Alert>
            )}
          </SpaceBetween>
        </Box>
      )}
    </div>
  );
};
